import React, { Suspense, useEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import PropTypes from 'prop-types'
import { Box, CircularProgress, Grow, IconButton } from '@material-ui/core'

import { SVG } from 'components'
import {
  BrowserToolbar,
  PreviewModeToggle,
  PrimaryLevelBanner,
  SecondLevelBanner,
} from './components'

import helpers from 'helpers'
import styles from './styles'
import constants from 'constants/index'

const BannerPreview = ({ openBanner, setOpenBanner, description }) => {
  const {
    BANNER_CENTER_POSITION,
    BANNER_FIXED_POSITION,
    BANNER_RIGHT_POSITION,
    BANNER_TOP_POSITION_PREFIX,
    DESKTOP_PREVIEW_WIDTH,
    PREVIEW_MODES,
  } = constants.cookies.banner

  const [openSecondLevel, setOpenSecondLevel] = useState(false)
  const [previewMode, setPreviewMode] = useState(PREVIEW_MODES.BANNER)
  const [screenScale, setScreenScale] = useState(1)

  const { watch } = useFormContext()

  const screenRef = useRef(null)

  const position = watch('position')
  const secondaryColor = helpers.palette.validateColor(watch('secondaryColor'))
  const privacyPortalEnabled = watch('privacyPortalEnabled')
  const privacyPortalUrl = watch('privacyPortalUrl')
  const isCompact = watch('isCompact')
  const hideRejectButton = watch('hideRejectButton')

  const isTop = !!position?.startsWith(BANNER_TOP_POSITION_PREFIX)
  // top_left, top_center... alinham na horizontal como left, center...
  const horizontalPosition = isTop
    ? position.slice(BANNER_TOP_POSITION_PREFIX.length)
    : position
  const isCenter = horizontalPosition === BANNER_CENTER_POSITION
  const isFixed = horizontalPosition === BANNER_FIXED_POSITION
  const isRight = horizontalPosition === BANNER_RIGHT_POSITION
  const isMobile = previewMode === PREVIEW_MODES.MOBILE

  const useStyles = styles({
    secondaryColor,
    previewMode,
    isFixed,
    isTop,
    screenScale,
  })
  const classes = useStyles()

  const handleOpenBanner = () => setOpenBanner((prevState) => !prevState)
  const handleOpenSecondLevel = () => setOpenSecondLevel(true)
  const handleCloseSecondLevel = () => setOpenSecondLevel(false)

  const bannerSize = () => {
    if (isFixed) return 'full'
    if (isMobile) return 'default'
    if (isCenter) return 'medium'
    if (position) return 'default'
    return undefined
  }

  useEffect(() => {
    const screen = screenRef.current
    if (!screen) return

    let animationFrame
    const observer = new ResizeObserver(([entry]) => {
      cancelAnimationFrame(animationFrame)
      animationFrame = requestAnimationFrame(() =>
        setScreenScale(entry.contentRect.width / DESKTOP_PREVIEW_WIDTH),
      )
    })
    observer.observe(screen)

    return () => {
      cancelAnimationFrame(animationFrame)
      observer.disconnect()
    }
  }, [DESKTOP_PREVIEW_WIDTH])

  return (
    <>
      <Box className={classes.root}>
        <PreviewModeToggle
          previewMode={previewMode}
          onChange={setPreviewMode}
        />
        <Box className={classes.previewArea}>
          <Box className={classes.frame}>
            {isMobile && <Box className={classes.phoneSpeaker} />}
            {previewMode === PREVIEW_MODES.DESKTOP && <BrowserToolbar />}
            <div ref={screenRef} className={classes.screen}>
              <Box
                key={`${position}-${previewMode}`}
                className={classes.screenContent}
              >
                {openBanner && (
                  <Grow in={openBanner}>
                    <Box
                      height="100%"
                      display="flex"
                      alignItems={isTop ? 'start' : 'end'}
                      justifyContent={
                        isMobile || isCenter
                          ? 'center'
                          : isRight
                          ? 'end'
                          : 'start'
                      }
                    >
                      <Suspense fallback={<CircularProgress />}>
                        <PrimaryLevelBanner
                          size={bannerSize()}
                          handleOpenBanner={handleOpenBanner}
                          description={description}
                          privacyPortalEnabled={privacyPortalEnabled}
                          privacyPortalUrl={privacyPortalUrl}
                          onManageCookies={handleOpenSecondLevel}
                          isCompact={isCompact}
                          hideRejectButton={hideRejectButton}
                          isMobile={isMobile}
                        />
                      </Suspense>
                    </Box>
                  </Grow>
                )}
                {!openBanner && (
                  <Grow in={!openBanner}>
                    <Box
                      height="100%"
                      display="flex"
                      alignItems={isTop ? 'start' : 'end'}
                      justifyContent={
                        isCenter ? 'center' : isRight ? 'end' : 'start'
                      }
                    >
                      <IconButton
                        onClick={handleOpenBanner}
                        aria-label="Abrir o gerenciador de cookies"
                        className={classes.cookieButton}
                      >
                        <SVG.CookieIcon className={classes.cookieIcon} />
                      </IconButton>
                    </Box>
                  </Grow>
                )}
              </Box>
            </div>
          </Box>
        </Box>
      </Box>

      <SecondLevelBanner
        handleCloseSecondLevel={handleCloseSecondLevel}
        openSecondLevel={openSecondLevel}
      />
    </>
  )
}

BannerPreview.propTypes = {
  openBanner: PropTypes.bool.isRequired,
  setOpenBanner: PropTypes.func.isRequired,
  description: PropTypes.string,
}

export default BannerPreview
